import { BelpostLegalInfo } from '$core/models/belpost-adapted-legal-info';

export enum CartTypes {
  subscription = 'internet-subscription',
}

import { Office } from '$modules/pages/post-office/office';
import {
  Publication,
  SubscriptionItem,
} from '$modules/pages/subscriptions/subscription';
import { OldAddress } from './old-address';
import {
  BelpostContact,
  BelpostShortContact,
} from '$shared/types/belpost-contact';
import { BelpostRepublicAddress } from '$shared/types/belpost-republic-address';

type PeriodsAddresses = Record<
  number,
  {
    delivery_object: BelpostContact<BelpostRepublicAddress>;
  }
>;

export class Basket {
  public id: number;
  public type: string;
  public maxAmount: number;
  public include_nds: number;
  public inCancellation: number[][] | null;
  public amount: number;
  public amount_to_cancel?: number;
  public created_at: string;
  public updated_at: string;
  public in_cancellation: Cancellation | null;
  public canceled: any[]; // TODO:
  public price: number;
  public basketable: Basketable;
  public cancellation_months: number[][];
  public months: number[][];
  public months_to_cancel: number[][];
  public delivery_type: string;
  public newDataForPublication;
  public newDataForPeriods;
  public local_id?: number;
  public index?: number;
  public periods_address: PeriodsAddresses | null;
  public basket_address: BelpostContact<BelpostRepublicAddress> | null;

  public get name() {
    return this.basketable.publication && this.basketable.publication.name
      ? this.basketable.publication.name
      : '';
  }

  public get user_type() {
    return this.basketable.user_type;
  }

  constructor(item?) {
    this.update(item);
  }

  public update(item): void {
    if (item) {
      Object.assign(this, item);
      if (item.basketable && item.basketable.publication) {
        item.basketable.publication = new Publication(
          item.basketable.publication
        );
      }
    }
  }
}

export class Cancellation {
  public data: {
    amount_to_cancel?: number;
    months_to_cancel?: number[][];
    possible_months_to_cancel?: any;
  };
  public id: number;
  public is_statement_file_available: boolean;
  public is_receipt_file_available: boolean;
  public is_invoice_file_available: boolean;
  public number: number | null;
  public price: string;
  public status: string;
  public type: 'cancellation';
  public contact_data: BelpostContact<BelpostRepublicAddress> | null;
}

export class Redirection {
  public data: {
    address_since: string;
    has_single_basket: boolean;
    has_sub_periods: boolean;
  };
  public id: number;
  public type: 'redirection';
  public status: string;
  public price: string;
  public number: string | null;
  public is_statement_file_available: boolean;
  public is_receipt_file_available: boolean;
  public is_invoice_file_available: boolean;
  public contact_data: BelpostContact<BelpostRepublicAddress> | null;
}
export class Basketable extends SubscriptionItem {
  public price: string;
  public extra_name?: any;
  public publication: Publication;
}

type SubscriptionPeriods = 1 | 2 | null;

export interface EstimatingRedirectionCostBody {
  data: RedirectionItemInfoDTO[];
}

export interface RedirectionItemInfoDTO {
  contact_data: BelpostShortContact;
  basket_id?: number; // ID корзины (опционально - если не указано, применяется ко всем корзинам заказа)
  periods?: number[][];
  payment_method: string;
}

export interface RedirectionItemInfo {
  contact_data: BelpostContact<BelpostRepublicAddress>;
  basket_id?: number; // ID корзины (опционально - если не указано, применяется ко всем корзинам заказа)
  periods?: number[][];
  payment_method: string;
}

/**
 * Класс заказа подписки
 * Представляет заказ на подписку на издания с полной информацией о заказе, оплате и доставке
 */
export class SubscriptionOrder {
  /**
   * Уникальный идентификатор заказа
   * */
  public id?: number;

  /**
   * Дата и время последнего обновления заказа
   * */
  public updated_at?: string;

  /**
   * Список модификаций заказа
   * */
  public modifications?: any[];

  /**
   * Тип доставки (address - по адресу, ops - в отделение почтовой связи)
   * */
  public delivery_type?: string;

  /**
   * Дата и время создания заказа
   * */
  public created_at?: string;

  /**
   * Статус заказа (payed - оплачен, in_cancellation - в процессе отмены и т.д.)
   * */
  public status: string;

  /**
   * Список перенаправлений заказа
   * */
  public redirected: any[];

  /**
   * Информация о юридическом лице (для корпоративных клиентов)
   * */
  public legal_info: BelpostLegalInfo;

  /**
   * Тип заказа
   * */
  public type: string;

  /**
   * Сумма расхождения
   * */
  public discrepancy_amount?: number | null;

  /**
   * Корзина с товарами заказа
   * */
  public baskets: Basket[];

  /**
   * Общая стоимость заказа
   * */
  public price: number;

  /**
   * Номер инкремента
   * */
  public increment_number?: number;

  /**
   * Тип пользователя (legal - юридическое лицо, individual - физическое лицо)
   * */
  public user_type: string;

  /**
   * Количество экземпляров
   * */
  public amount: number;

  /**
   * Информация об отмене заказа
   * */
  public in_cancellation: Cancellation | null;

  /**
   * Информация о перенаправлении заказа
   * */
  public in_redirection: Redirection | null;

  /**
   * Номер заказа
   * */
  public number: string | null;

  /**
   * Требуется ли адрес доставки
   * */
  public need_address: boolean;

  /**
   * Требуется ли отделение почтовой связи
   * */
  public need_ops: boolean;

  /**
   * Можно ли повторить заказ
   * */
  public can_repeat_order?: boolean;

  /**
   * Доступен ли файл расхождения
   * */
  public is_discrepancy_file_available: boolean;

  /**
   * Доступен ли информационный файл
   * */
  public is_info_file_available: boolean;

  /**
   * Доступен ли файл квитанции
   * */
  public is_receipt_file_available: boolean;

  /**
   * Доступен ли файл счета
   * */
  public is_invoice_file_available: boolean;

  /**
   * Требуется ли абонентский ящик
   * */
  public need_ops_box: boolean;

  /**
   * Требуется ли виртуальная подписка
   * */
  public need_virtual: boolean;

  /**
   * Название организации или ФИО заказчика
   * */
  public name: string;

  /**
   * Информация о доставке
   * */
  public delivery_info: BelpostContact<BelpostRepublicAddress>;

  /**
   * Идентификатор пользователя
   * */
  public user_id: number;

  /**
   * Дата истечения подписки
   * */
  public expired_at: string;

  /**
   * Дата оплаты заказа
   * */
  public payed_at: string;

  /**
   * Номер платежного поручения
   * */
  public payment_order_number: string;

  /** Период подписки (1 - полугодие, 2 - год) */
  public period: SubscriptionPeriods;

  /** Год периода подписки */

  public year_period: number;

  /**
   * Конструктор класса заказа подписки
   * @param item - объект с данными заказа для инициализации
   * @param forceStatus - принудительная установка статуса заказа
   */
  constructor(item?, forceStatus?) {
    this.update(item, forceStatus);
  }

  /**
   * Обновление данных заказа
   * @param item - объект с новыми данными заказа
   * @param forceStatus - принудительная установка статуса заказа
   */
  public update(item, forceStatus?): void {
    if (item && !(item instanceof SubscriptionOrder)) {
      Object.assign(this, item);
      if (forceStatus) {
        this.status = forceStatus;
      }
      if (this.baskets) {
        this.baskets = this.baskets.map((el) => {
          const b = new Basket(el);
          if (b.in_cancellation) {
            this.status = 'in_cancellation';
          }
          return b;
        });
      }
    }
  }
}
